import { useMemo } from 'react';

import type { useAlertActionsController } from '@atlassian/eoc-alert-common';

import commonMessages from '../../../messages';
import { type TLineItemAction } from '../../../types';

type BulkLoadingState = {
	isBulkAckLoading?: boolean;
	isBulkAddResponderLoading?: boolean;
	isBulkAssignLoading?: boolean;
	isBulkCloseLoading?: boolean;
	isBulkDeleteLoading?: boolean;
	isBulkSnoozeLoading?: boolean;
	isBulkTakeOwnershipLoading?: boolean;
	isBulkUnacknowledgeLoading?: boolean;
	processingBulkIds: string[];
};

export interface UseAllActionsParams {
	actionsController: ReturnType<typeof useAlertActionsController>;
	alertId: string;
	bulkLoadingState: BulkLoadingState;
}

/**
 * Shared hook that generates the complete list of available alert actions.
 * This centralizes the business logic for creating action definitions to avoid
 * duplication between different action components (summary, inline, etc.).
 */
export const useAllActions = ({
	alertId,
	bulkLoadingState,
	actionsController,
}: UseAllActionsParams): TLineItemAction[] => {
	const {
		processingBulkIds,
		isBulkSnoozeLoading,
		isBulkAssignLoading,
		isBulkCloseLoading,
		isBulkDeleteLoading,
		isBulkUnacknowledgeLoading,
		isBulkAckLoading,
		isBulkAddResponderLoading,
		isBulkTakeOwnershipLoading,
	} = bulkLoadingState;

	const {
		handleAck,
		handleClose,
		handleAssign,
		handleTakeOwnership,
		handleEscalate,
		handleAddResponder,
		handleSnooze,
		handleDelete,
		handleUnack,
		isAssigneeUpdateLoading,
		isRespondersUpdateLoading,
		isAckLoading,
		isUnackLoading,
		isDeleteLoading,
		isSnoozeLoading,
		isCloseLoading,
		isEscalateLoading,
	} = actionsController;

	const isBulkProcessing = useMemo(
		() => processingBulkIds.includes(alertId),
		[processingBulkIds, alertId],
	);

	const allActions: TLineItemAction[] = [
		{
			label: commonMessages.acknowledge,
			onClick: handleAck,
			actionId: 'acknowledge',
			isLoading: isAckLoading || (isBulkProcessing && isBulkAckLoading),
		},
		{
			label: commonMessages.close,
			onClick: handleClose,
			actionId: 'close',
			isLoading: isCloseLoading || (isBulkProcessing && isBulkCloseLoading),
		},
		{
			label: commonMessages.assign,
			onClick: handleAssign,
			actionId: 'assign',
			isLoading: isAssigneeUpdateLoading || (isBulkProcessing && isBulkAssignLoading),
		},
		{
			label: commonMessages.unacknowledge,
			onClick: handleUnack,
			actionId: 'unacknowledge',
			isLoading: isUnackLoading || (isBulkProcessing && isBulkUnacknowledgeLoading),
		},
		{
			label: commonMessages.takeOwnership,
			onClick: handleTakeOwnership,
			actionId: 'takeOwnership',
			isLoading: isAssigneeUpdateLoading || (isBulkProcessing && isBulkTakeOwnershipLoading),
		},
		{
			label: commonMessages.escalateToNext,
			onClick: handleEscalate,
			actionId: 'escalateToNext',
			isLoading: isEscalateLoading,
		},
		{
			label: commonMessages.addResponder,
			onClick: handleAddResponder,
			actionId: 'addResponder',
			isLoading: isRespondersUpdateLoading || (isBulkProcessing && isBulkAddResponderLoading),
		},
		{
			label: commonMessages.snooze,
			onClick: handleSnooze,
			actionId: 'snooze',
			isLoading: isSnoozeLoading || (isBulkProcessing && isBulkSnoozeLoading),
		},
		{
			label: commonMessages.delete,
			onClick: handleDelete,
			actionId: 'delete',
			isLoading: isDeleteLoading || (isBulkProcessing && isBulkDeleteLoading),
		},
	];

	return allActions;
};
